// Packing-proof panel: the SwiftLOG order on the table, the running photo
// checks (filler, carton layer, fixation beams) and the proof photos taken for
// this order. Visible only for workflows that declare `packing_proof`.
//
// Pure rendering: LiveSession feeds it the Trainer context, the backend's
// packing_status / proof_result messages and the captured stills.

import { t } from "./i18n.js";

const MAX_PROOF_TILES = 6;
const CHECK_ORDER = ["filler", "cover", "beams"];

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = text;
  return node;
}

function clock(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

export class PackingPanel {
  constructor({ container, onCapture } = {}) {
    this.container = container || null;
    this.onCapture = onCapture;
    this.enabled = false;
    this.minBeams = 2;
    this.trainerAvailable = false;
    this.context = null; // Trainer's wms snapshot
    this.status = null; // backend packing_status
    this.proofs = []; // [{ result, imageUrl, saved }]
    this.checking = false;
  }

  setWorkflow(workflow) {
    const cfg = workflow && workflow.packing_proof;
    this.enabled = !!cfg;
    this.minBeams = cfg && Number.isFinite(cfg.min_fixation_beams) ? cfg.min_fixation_beams : 2;
    this.render();
  }

  setTrainerAvailable(available) {
    this.trainerAvailable = !!available;
    this.render();
  }

  setContext(context) {
    this.context = context && context.wms ? context.wms : null;
    this.render();
  }

  setStatus(status) {
    const previousKey = this.status && this.status.order_key;
    this.status = status || null;
    // A new (or completed) order starts a fresh photo strip; the finished
    // order stays summarized in the "last order" line.
    if (status && previousKey && status.order_key !== previousKey) this.proofs = [];
    this.render();
  }

  setChecking(checking) {
    this.checking = !!checking;
    this.render();
  }

  addProof(result, imageUrl) {
    this.proofs.unshift({ result, imageUrl: imageUrl || null, saved: null });
    this.proofs = this.proofs.slice(0, MAX_PROOF_TILES);
    this.render();
  }

  markSaved(recordId, saveResult) {
    const entry = this.proofs.find((p) => p.result && p.result.record_id === recordId);
    if (!entry) return;
    entry.saved = saveResult || { ok: false };
    this.render();
  }

  render() {
    const root = this.container;
    if (!root) return;
    root.hidden = !this.enabled;
    if (!this.enabled) return;
    root.replaceChildren();

    const header = el("div", "packing-header");
    header.append(el("strong", null, t("packing.title")));
    header.append(
      el(
        "span",
        `packing-trainer ${this.trainerAvailable ? "on" : "off"}`,
        this.trainerAvailable ? t("packing.trainer_connected") : t("packing.trainer_missing"),
      ),
    );
    root.append(header, this._renderOrder(), this._renderChecks(), this._renderProofs());

    const actions = el("div", "packing-actions");
    const capture = el("button", "ghost packing-capture", t("packing.capture"));
    capture.type = "button";
    capture.addEventListener("click", () => {
      if (typeof this.onCapture === "function") this.onCapture();
    });
    actions.append(capture);
    const last = this.status && this.status.last_order;
    if (last && last.order) {
      actions.append(
        el(
          "span",
          "packing-last-order",
          t("packing.last_order", {
            id: last.order.identifier || "?",
            count: (last.proofs || []).length,
          }),
        ),
      );
    }
    root.append(actions);
  }

  _renderOrder() {
    const box = el("div", "packing-order");
    const wms = this.context;
    const order = (this.status && this.status.order) || {};
    const identifier = (wms && wms.identifier) || order.identifier;
    if (!identifier || (wms && wms.phase === "idle")) {
      box.append(el("span", "packing-muted", t("packing.order_none")));
      return box;
    }
    const line = el("div", "packing-order-line");
    line.append(el("b", null, identifier));
    const shipment = (wms && wms.shipment) || order.shipment;
    if (shipment) line.append(el("span", null, shipment));
    const progress = (wms && wms.progress) || (this.status && this.status.progress) || {};
    if (progress.total) {
      const done = progress.packed >= progress.total;
      line.append(
        el(
          "span",
          `packing-units ${done ? "done" : ""}`,
          t("packing.units", { packed: progress.packed ?? 0, total: progress.total }),
        ),
      );
    }
    box.append(line);
    const container = (wms && wms.container) || {};
    const parts = [
      container.number || order.container_number,
      container.type || order.carton_type,
      container.weightKg || order.weight_kg ? `${container.weightKg || order.weight_kg} kg` : null,
    ].filter(Boolean);
    if (parts.length) box.append(el("div", "packing-muted", parts.join(" · ")));
    return box;
  }

  _renderChecks() {
    const box = el("div", "packing-checks");
    const checks = (this.status && this.status.checks) || {};
    for (const key of CHECK_ORDER) {
      const state = checks[key] || "unknown";
      const chip = el("span", `packing-check state-${state}`);
      let value = t(`packing.state.${state}`);
      if (key === "beams" && Number.isFinite(checks.beam_count)) {
        value = `${checks.beam_count}/${this.minBeams}`;
      }
      chip.append(el("span", "packing-check-label", t(`packing.check.${key}`)), el("b", null, value));
      box.append(chip);
    }
    if (this.checking) box.append(el("span", "packing-muted", t("packing.checking")));
    const gaps = checks.filler === "missing" ? checks.filler_gaps : "";
    const last = this.status && this.status.last_assessment;
    const note = gaps || (last && last.summary) || "";
    if (note) box.append(el("div", "packing-note", note));
    return box;
  }

  _renderProofs() {
    const box = el("div", "packing-proofs");
    box.append(el("div", "packing-subtitle", t("packing.proofs")));
    if (!this.proofs.length) {
      box.append(el("span", "packing-muted", t("packing.proofs_none")));
      return box;
    }
    const strip = el("div", "packing-proof-strip");
    for (const { result, imageUrl, saved } of this.proofs) {
      const tile = el("figure", `packing-proof verdict-${result.verdict || "info"}`);
      if (imageUrl) {
        const img = el("img");
        img.src = imageUrl;
        img.alt = result.stage || "";
        tile.append(img);
      }
      const caption = el("figcaption");
      caption.append(
        el("b", null, t(`packing.stage.${result.stage}`)),
        el("span", null, `${clock(result.captured_at_ms)}${saved && saved.ok ? ` · ${t("packing.saved")}` : ""}`),
      );
      tile.title = result.message || "";
      tile.append(caption);
      strip.append(tile);
    }
    box.append(strip);
    return box;
  }
}
